import ChevronRightIcon from '@mui/icons-material/ChevronRightOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useState } from 'react';

import { CSVColumn } from '~utils/csv/csvProcessor';

import { EMPTY_CELL_PLACEHOLDER } from './constants';
import { CSVColumnLabelCell } from './CSVColumnLabelCell';
import { TIMEZONE_REFERENCE } from './timezoneReference';

interface CSVFieldReferenceProps {
  columns: CSVColumn[];
  entityType: string;
}

export function CSVFieldReference({ columns, entityType }: CSVFieldReferenceProps) {
  const [expanded, setExpanded] = useState(false);
  const isUsersEntityType = entityType === 'users';
  const isDriversEntityType = entityType === 'drivers';
  const showTimezoneReference = isUsersEntityType || isDriversEntityType;

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'action.hover' },
          borderRadius: 1,
          p: 0.5,
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <IconButton size="small" sx={{ p: 0, mr: 1 }}>
          {expanded ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </IconButton>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t('csv_import_export.field_reference.title')}
        </Typography>
      </Box>

      <Collapse in={expanded}>
        <TableContainer
          sx={{
            maxHeight: 360,
            mt: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('csv_import_export.field_reference.column')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('csv_import_export.field_reference.header')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('csv_import_export.field_reference.description')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('csv_import_export.field_reference.example')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {columns.map((column) => (
                <TableRow key={column.key}>
                  <CSVColumnLabelCell column={column} />
                  <TableCell
                    sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', fontSize: 13 }}
                  >
                    {column.header}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {column.description || EMPTY_CELL_PLACEHOLDER}
                    </Typography>
                    {Boolean(column.enumValues?.length) && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', display: 'block' }}
                      >
                        {t('csv_import_export.field_reference.one_of', {
                          values: column.enumValues?.join(', '),
                        })}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                    {column.exampleValue ||
                      column.enumValues?.[0] ||
                      EMPTY_CELL_PLACEHOLDER}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {showTimezoneReference && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              {t('csv_import_export.field_reference.timezone_title')}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 0.5,
                border: 1,
                borderColor: 'divider',
                p: 1,
                borderRadius: 1,
                maxHeight: 180,
                overflow: 'auto',
              }}
            >
              {TIMEZONE_REFERENCE.map((timezone) => (
                <Box key={timezone.value} sx={{ display: 'flex', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', minWidth: 160, fontSize: 13 }}
                  >
                    {timezone.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {timezone.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Collapse>
    </Box>
  );
}
